import { IntlShape } from "react-intl";
import Messages from "../localization/Messages";
import { DateFormat, getDateText } from "./../helpers/dateHelper";
import { displayAmount } from "./../helpers/moneyHelper";
import { IAddress, addressInit } from "./address";
import { IBooking } from "./booking";
import { IShipDispatcher } from "./shipDispatcher";

export interface IInvoice {
    id: number;
    addressId: string;
    address: IAddress;
    bookingId?: number;
    companyId: string;
    deleted: number;
    internalInvoiceNumber?: string;
    invoiceDate?: number | null;
    invoiceReferenceField?: string;
    invoiceStatus?: InvoiceStatus;
    invoicedExpenses?: string[];
    shipId?: string;
    totalCostGross: number;
    totalCostNet: number;
    invoiceCompanyName?: string;
    companyName?: string;
    currency?: string;
    booking?: IBooking;
    deletable?: boolean;
    createTs?: number;
    shipName?: string;
    bookingFinalized?: boolean;
    allowEarlyInvoicing?: boolean;
}

export type IEditInvoice = Omit<IInvoice, "booking">;

export const invoiceInit: IInvoice = {
    id: 0,
    addressId: "",
    address: addressInit,
    companyId: "",
    deleted: 0,
    totalCostGross: 0,
    totalCostNet: 0,
};

export enum InvoiceStatus {
    OPEN = "OPEN",
    READY_FOR_BILLING = "READY_FOR_BILLING",
    INVOICED = "INVOICED",
    CANCELLED = "CANCELLED",
    DELETED = "DELETED",
    NOT_ASSIGNED = "NOT_ASSIGNED",
}

export function isInvoiceEditable(invoiceStatus?: InvoiceStatus): boolean {
    return (
        !!invoiceStatus &&
        (invoiceStatus === InvoiceStatus.OPEN || invoiceStatus === InvoiceStatus.READY_FOR_BILLING)
    );
}

export const InvoiceStatuses = [
    { id: 0, value: InvoiceStatus.OPEN, label: Messages.open },
    { id: 1, value: InvoiceStatus.READY_FOR_BILLING, label: Messages.readyForBilling },
    { id: 2, value: InvoiceStatus.INVOICED, label: Messages.invoiced },
    { id: 3, value: InvoiceStatus.CANCELLED, label: Messages.canceled },
    { id: 4, value: InvoiceStatus.DELETED, label: Messages.deleted },
    { id: 5, value: InvoiceStatus.NOT_ASSIGNED, label: Messages.notAssigned },
];

export const displayInvoiceStatus = (intl: IntlShape, status: InvoiceStatus | undefined) => {
    const statusText = InvoiceStatuses.find(s => s.value === status)?.label;
    return statusText ? intl.formatMessage(statusText) : "";
};

export const displayBookingInfo = (row: IInvoice) => {
    if (row.booking) {
        const { arrivalTime, departureTime, actualArrivalTime, actualDepartureTime } = row.booking;
        const allowEarlyInvoicing = row.allowEarlyInvoicing ?? false;
        //const shipName =
        //    ships.find(ship => ship.id === row.shipId)?.name ?? row.booking.shipName ?? "";
        const arrivalTimeDisplay = allowEarlyInvoicing
            ? arrivalTime
            : actualArrivalTime
            ? actualArrivalTime
            : arrivalTime;
        const departureTimeDisplay = allowEarlyInvoicing
            ? departureTime
            : actualDepartureTime
            ? actualDepartureTime
            : departureTime;
        return `${getDateText(arrivalTimeDisplay, DateFormat.CLIENT_DATE_TIME)} - ${getDateText(
            departureTimeDisplay,
            DateFormat.CLIENT_DATE_TIME,
        )}`;
    }
};

export const getInvoiceCsvMapper = (intl: IntlShape, ships: IShipDispatcher[]) => {
    return (e: IInvoice) => ({
        invoiceDate: getDateText(e.invoiceDate, DateFormat.CLIENT_DATE),
        invoiceStatus: displayInvoiceStatus(intl, e.invoiceStatus),
        invoiceCompanyName: e.invoiceCompanyName ?? "",
        accountsReceivableNumber: (e.address as any)?.accountRecNumber ?? "",
        ship: ships.find(ship => ship.id === e.shipId)?.name ?? e.booking?.shipName ?? "",
        bookingInformation: displayBookingInfo(e),
        totalCostNet: displayAmount(e.totalCostNet ?? 0),
        bookingFinalized: intl.formatMessage(e.bookingFinalized ? Messages.yes : Messages.no),
        comment: e.booking?.comment ?? "",
        sharedRemark: e.booking?.sharedRemark ?? "",
    });
};
